import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  FiChevronDown,
  FiCopy,
  FiEye,
  FiDownload,
  FiX,
} from "react-icons/fi";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa6";
import {
  LuArrowDown,
  LuArrowUp,
  LuCheckCircle,
  LuSlidersHorizontal,
  LuWallet,
  LuWalletCards,
} from "react-icons/lu";
import PayoutDataTable from "./PayoutDataTable";
import PayoutEmptyState from "./PayoutEmptyState";
import PayoutDateRangeFilter from "./PayoutDateRangeFilter";
import PayoutDropdown from "./PayoutDropdown";
import PayoutFilterActions from "./PayoutFilterActions";
import PayoutFilterChips from "./PayoutFilterChips";
import PayoutSearchFilter from "./PayoutSearchFilter";
import PayoutTableCell from "./PayoutTableCell";
import { parsePayoutTableDate } from "./payoutDateUtils";
import { payoutApi } from "./api/payoutApiClient";
import {
  accountFromApiRecord,
  formatWireDate,
  statementRowFromApiRecord,
  statementSummaryFromApi,
  type StatementSummaryCard,
} from "./api/payoutApiAdapters";
import { usePayoutResource } from "./api/usePayoutResource";
import type { PayoutAccount } from "./payoutData";
import { usePayoutAccess } from "./payoutAccessControl";
const formatINR = (amount: number) =>
  `\u20B9${amount.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const statementSummary = [
  {
    label: "Opening Balance",
    value: 0,
    detail: "In selected period",
    icon: <LuWalletCards />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
  {
    label: "Total Credits",
    value: 0,
    detail: "In selected period",
    icon: <LuArrowDown />,
    tone: "bg-[#16A34A1A] text-[#16A34A]",
  },
  {
    label: "Total Debits",
    value: 0,
    detail: "In selected period",
    icon: <LuArrowUp />,
    tone: "bg-[#EF44441A] text-[#EF4444]",
  },
  {
    label: "Closing Balance",
    value: 0,
    detail: "In selected period",
    icon: <LuWallet />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
];

const statementRows: StatementDetailsRow[] = [];

const statusClassName: Record<string, string> = {
  Processed: "bg-[#DCFCE7] text-[#16A34A]",
  Pending: "bg-[#FEF3C7] text-[#F59E0B]",
  Failed: "bg-[#FEE2E2] text-[#EF4444]",
};

const typeClassName: Record<string, string> = {
  Credit: "bg-[#DCFCE7] text-[#16A34A]",
  Debit: "bg-[#FEE2E2] text-[#EF4444]",
};

export type StatementDetailsRow = {
  dateTime: string;
  referenceId: string;
  utrNumber: string;
  beneficiary?: string;
  beneficiaryAccountNumber: string;
  narration: string;
  account: string;
  accountNumber: string;
  type: string;
  paymentMode?: string;
  channel?: string;
  referenceRemark?: string;
  initiatedBy?: string;
  approvedBy?: string;
  approvedOn?: string;
  completedOn?: string;
  amount: number;
  balance: number;
  status: string;
};

function FilterSelect({
  label,
  value,
  options,
  onChange,
  wide = false,
  className = "",
}: {
  label?: string;
  value: string;
  options?: string[];
  onChange?: (value: string) => void;
  wide?: boolean;
  className?: string;
}) {
  return (
    <PayoutDropdown
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      wide={wide}
      className={className || (wide ? "" : "min-w-[210px]")}
    />
  );
}

function DetailMetric({
  label,
  value,
  helper,
  icon,
  tone,
}: {
  label: string;
  value: string;
  helper?: string;
  icon: React.ReactNode;
  tone: string;
}) {
  return (
    <div className="flex min-w-[180px] items-center gap-4 border-r border-[#E0E5F2] px-5 py-2 last:border-r-0">
      <span
        className={
          "payout-kpi-icon flex h-10 w-10 shrink-0 items-center justify-center text-lg " +
          tone
        }
      >
        {icon}
      </span>
      <span>
        <span className="block text-xs font-semibold text-[#505E8E]">
          {label}
        </span>
        <span className="mt-1 block text-sm font-semibold text-[#1B163B]">
          {value}
        </span>
        {helper && (
          <span className="mt-1 block text-xs font-medium text-[#505E8E]">
            {helper}
          </span>
        )}
      </span>
    </div>
  );
}

function DetailLine({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="grid gap-4 border-b border-[#E0E5F2] py-3 text-sm md:grid-cols-[190px_minmax(0,1fr)]">
      <span className="font-medium text-[#505E8E]">{label}</span>
      <div className="font-semibold text-[#1B163B]">{value}</div>
    </div>
  );
}

export function StatementDetailsDrawer({
  row,
  onClose,
}: {
  row: StatementDetailsRow | null;
  onClose: () => void;
}) {
  useEffect(() => {
    if (!row) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [row]);

  if (!row) return null;

  const amount = Math.abs(row.amount);
  const referenceId = row.referenceId;
  const emptyValue = "-";
  const transactionLabel = row.type ? `${row.type} Transaction` : "";
  const sourceAccountDisplay =
    [row.account, row.accountNumber].filter(Boolean).join(" - ") || emptyValue;

  const downloadStatement = () => {
    const content = [
      "ClearN Statement",
      `Reference ID: ${referenceId}`,
      `Narration: ${row.narration}`,
      `Account: ${row.account} **** ${row.accountNumber}`,
      `Amount: ${formatINR(amount)}`,
      `Status: ${row.status}`,
      `Date: ${row.dateTime}`,
    ].join("\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${referenceId}-statement.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const copyReference = () => {
    navigator.clipboard?.writeText(referenceId);
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex justify-end bg-[#1B163B]/20 backdrop-blur-[2px]">
      <button
        type="button"
        aria-label="Close statement details"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <aside className="payout-detail-drawer relative z-10 h-full w-full max-w-[1180px] overflow-y-auto bg-white px-8 py-7 shadow-2xl">
        <div className="mb-7 flex items-start justify-between gap-5">
          <div>
            <h2 className="text-[22px] font-semibold text-[#1B163B]">
              Statement Details
            </h2>
            <span className="mt-4 inline-flex rounded-[6px] bg-[#DCFCE7] px-3 py-1 text-xs font-semibold text-[#16A34A]">
              {row.status || emptyValue}
            </span>
            <p className="mt-3 text-xs font-semibold text-[#505E8E]">
              Reference ID
            </p>
            <div className="mt-1 flex items-center gap-2 text-lg font-semibold text-[#1B163B]">
              {referenceId || emptyValue}
              <button type="button" onClick={copyReference}>
                <FiCopy className="text-sm text-[#505E8E]" />
              </button>
            </div>
            <p className="mt-2 text-sm font-medium text-[#505E8E]">
              {transactionLabel || emptyValue} {" • "}
              Initiated on {row.dateTime || emptyValue}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={downloadStatement}
              className="flex h-10 items-center gap-3 rounded-[6px] border border-[#BBD1FF] bg-white px-5 text-sm font-semibold text-[#2563EB]"
            >
              <FiDownload className="text-lg" />
              Download Statement
            </button>
            <button
              type="button"
              onClick={onClose}
              className="text-2xl text-[#1B163B]"
            >
              <FiX />
            </button>
          </div>
        </div>

        <section className="mb-5 overflow-x-auto rounded-[8px] border border-[#E0E5F2] bg-white py-5 new-shadow">
          <div className="flex min-w-[980px] items-center justify-between">
            <DetailMetric
              label="Amount"
              value={formatINR(amount)}
              icon={<LuWalletCards />}
              tone="bg-[#6687FF1A] text-[#6687FF]"
            />
            <DetailMetric
              label="Fees & Charges"
              value={formatINR(0)}
              icon={<LuWallet />}
              tone="bg-[#6687FF1A] text-[#6687FF]"
            />
            <DetailMetric
              label="Net Amount"
              value={formatINR(amount)}
              icon={<LuSlidersHorizontal />}
              tone="bg-[#16A34A1A] text-[#16A34A]"
            />
            <DetailMetric
              label="Status"
              value={row.status || emptyValue}
              icon={<LuCheckCircle />}
              tone="bg-[#16A34A1A] text-[#16A34A]"
            />
            <DetailMetric
              label="Completed On"
              value={row.completedOn || emptyValue}
              icon={<LuWallet />}
              tone="bg-[#6687FF1A] text-[#6687FF]"
            />
          </div>
        </section>

        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_520px]">
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h3 className="mb-4 text-lg font-semibold text-[#1B163B]">
              Details
            </h3>
            <DetailLine
              label="Source Account"
              value={
                <span>
                  {sourceAccountDisplay}
                </span>
              }
            />
            <DetailLine
              label="Beneficiary"
              value={
                <span>
                  {row.beneficiaryAccountNumber ? (
                    <>
                      {row.beneficiary || emptyValue}
                      <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                        A/C No: {row.beneficiaryAccountNumber}
                      </span>
                    </>
                  ) : (
                    row.beneficiary || emptyValue
                  )}
                </span>
              }
            />
            <DetailLine label="Transfer Type" value={row.type || emptyValue} />
            <DetailLine label="Transfer Date" value={row.dateTime || emptyValue} />
            <DetailLine label="Purpose / Narration" value={row.narration || emptyValue} />
            <DetailLine label="Reference / Remark" value={row.referenceRemark || emptyValue} />
            <DetailLine label="UTR / Bank Reference No." value={row.utrNumber || emptyValue} />
            <DetailLine label="Payment Mode" value={row.paymentMode || emptyValue} />
            <DetailLine label="Channel" value={row.channel || emptyValue} />
            <DetailLine label="Initiated By" value={row.initiatedBy || emptyValue} />
            <DetailLine label="Approved By" value={row.approvedBy || emptyValue} />
            <DetailLine label="Approved On" value={row.approvedOn || emptyValue} />
          </section>

          <div className="space-y-5">
            <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
              <h3 className="mb-5 text-lg font-semibold text-[#1B163B]">
                Amount Breakdown
              </h3>
              <div className="space-y-4 text-sm">
                <div className="flex justify-between">
                  <span className="font-medium text-[#505E8E]">Transfer Amount</span>
                  <span className="font-semibold text-[#1B163B]">{formatINR(amount)}</span>
                </div>
                <div className="flex justify-between border-b border-dashed border-[#A3AED0] pb-5">
                  <span className="font-medium text-[#505E8E]">Fees & Charges</span>
                  <span className="font-semibold text-[#1B163B]">{formatINR(0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-semibold text-[#1B163B]">Net Amount</span>
                  <span className="font-semibold text-[#1B163B]">{formatINR(amount)}</span>
                </div>
              </div>
            </section>

            <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
              <h3 className="mb-5 text-lg font-semibold text-[#1B163B]">
                Attachments
              </h3>
              <div className="min-h-[61px] text-sm font-semibold text-[#1B163B]">
                {emptyValue}
              </div>
            </section>
          </div>
        </div>

        <div className="mt-8 flex justify-end border-t border-[#E0E5F2] pt-6">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
          >
            Close
          </button>
        </div>
      </aside>
    </div>,
    document.body
  );
}

function PayoutStatements() {
  const { canAccess } = usePayoutAccess();
  const defaultDateRange = useMemo(
    () => ({
      startDate: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000),
      endDate: new Date(),
      key: "selection",
    }),
    [],
  );
  const [accountFilter, setAccountFilter] = useState("All Accounts");
  const [transactionTypeFilter, setTransactionTypeFilter] = useState("All");
  const [creditDebitFilter, setCreditDebitFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("Reference ID");
  const [dateRangeFilter, setDateRangeFilter] = useState(defaultDateRange);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStatement, setSelectedStatement] = useState<StatementDetailsRow | null>(null);
  const pageSize = 10;
  const [appliedFilters, setAppliedFilters] = useState({
    account: "All Accounts",
    transactionType: "All",
    creditDebit: "All",
    status: "All",
    search: "",
    searchField: "Reference ID",
    dateRange: defaultDateRange,
  });
  useEffect(() => {
    setAppliedFilters({
      account: accountFilter,
      transactionType: transactionTypeFilter,
      creditDebit: creditDebitFilter,
      status: statusFilter,
      search: searchQuery,
      searchField,
      dateRange: dateRangeFilter,
    });
    setCurrentPage(1);
  }, [
    accountFilter,
    creditDebitFilter,
    dateRangeFilter,
    searchField,
    searchQuery,
    statusFilter,
    transactionTypeFilter,
  ]);
  const statementQueryKey = useMemo(
    () => ({
      account: appliedFilters.account,
      from: formatWireDate(appliedFilters.dateRange.startDate),
      to: formatWireDate(appliedFilters.dateRange.endDate),
    }),
    [appliedFilters],
  );
  const accountsResource = usePayoutResource(
    [] as PayoutAccount[],
    (signal) =>
      payoutApi.accounts(signal).then((accounts) =>
        accounts.map((account) =>
          accountFromApiRecord(account, {
            id: "",
            name: "",
            school: "",
            bank: "",
            logoUrl: "",
            accountNumber: "",
            ifsc: "",
            currency: "INR",
            addedOn: "",
            availableBalance: 0,
            accountBalance: 0,
            fundsOnHold: 0,
            overdraftBalance: 0,
            overdraftLimit: 0,
            lowBalanceThreshold: 0,
            accent: "",
          }),
        ),
      ),
  );
  const emptyStatementView = useMemo(
    (): { rows: typeof statementRows; summary: StatementSummaryCard[] } => ({
      rows: [],
      summary: (statementSummary as StatementSummaryCard[]).map((item) => ({
        ...item,
        value: 0,
        detail: "In selected period",
      })),
    }),
    [],
  );
  const statementsResource = usePayoutResource(
    emptyStatementView,
    async (signal) => {
      const apiAccounts = await payoutApi.accounts(signal);
      const accounts = apiAccounts.map((account) =>
        accountFromApiRecord(account, {
          id: "",
          name: "",
          school: "",
          bank: "",
          logoUrl: "",
          accountNumber: "",
          ifsc: "",
          currency: "INR",
          addedOn: "",
          availableBalance: 0,
          accountBalance: 0,
          fundsOnHold: 0,
          overdraftBalance: 0,
          overdraftLimit: 0,
          lowBalanceThreshold: 0,
          accent: "",
        }),
      );
      const selectedAccount =
        statementQueryKey.account === "All Accounts"
          ? accounts[0]
          : accounts.find(
              (account) =>
                account.name === statementQueryKey.account ||
                account.id === statementQueryKey.account,
            );

      if (!selectedAccount) {
        return emptyStatementView;
      }

      const envelope = await payoutApi.accountStatementEnvelope(
        selectedAccount.id,
        {
          from: statementQueryKey.from,
          to: statementQueryKey.to,
          limit: 125,
        },
        signal,
      );
      const items = Array.isArray(envelope.items)
        ? (envelope.items as Record<string, unknown>[])
        : [];
      const rows = items.map((row) =>
        statementRowFromApiRecord(row, {
          dateTime: "",
          referenceId: "",
          utrNumber: "",
          beneficiary: "",
          beneficiaryAccountNumber: "",
          narration: "",
          account: selectedAccount.name,
          accountNumber: selectedAccount.accountNumber,
          type: "",
          paymentMode: "",
          channel: "",
          referenceRemark: "",
          initiatedBy: "",
          approvedBy: "",
          approvedOn: "",
          completedOn: "",
          amount: 0,
          balance: 0,
          status: "",
        }),
      );
      const periodDetail = "In selected period";
      const summaryCards = statementSummaryFromApi(
        (envelope.summary as Record<string, unknown>) || {},
        statementSummary,
        periodDetail,
      );
      return { rows, summary: summaryCards };
    },
    [statementQueryKey],
  );
  const baseStatementRows = statementsResource.data.rows;
  const visibleStatementSummary =
    statementsResource.data.summary?.length > 0
      ? statementsResource.data.summary
      : emptyStatementView.summary;

  const expandedStatementRows = useMemo(
    () => {
      if (!baseStatementRows.length) return [];
      return baseStatementRows;
    },
    [baseStatementRows],
  );

  const accountOptions = useMemo(
    () => {
      return [
        "All Accounts",
        ...accountsResource.data.map((account) => account.name).filter(Boolean),
      ];
    },
    [accountsResource.data],
  );
  const statementSearchOptions = [
    "Reference ID",
    "UTR Number",
    "Beneficiary Account Number",
    "Beneficiary Name",
  ];

  const getBeneficiaryNameFromNarration = (narration: string) => {
    const parts = narration.split(" - ");
    return parts.length > 1 ? parts[parts.length - 1] : narration;
  };

  const getStatementSearchValue = (
    row: (typeof expandedStatementRows)[number],
    field: string,
  ) => {
    switch (field) {
      case "UTR Number":
        return row.utrNumber;
      case "Beneficiary Account Number":
        return row.beneficiaryAccountNumber;
      case "Beneficiary Name":
        return getBeneficiaryNameFromNarration(row.narration);
      case "Reference ID":
      default:
        return row.referenceId;
    }
  };

  const filteredRows = useMemo(() => {
    const query = appliedFilters.search.trim().toLowerCase();
    return expandedStatementRows.filter((row) => {
      const matchesAccount =
        appliedFilters.account === "All Accounts" ||
        row.account === appliedFilters.account;
      const matchesStatus =
        appliedFilters.status === "All" ||
        row.status === appliedFilters.status;
      const rowDate = parsePayoutTableDate(row.dateTime);
      const matchesDate =
        rowDate !== null &&
        rowDate >= appliedFilters.dateRange.startDate &&
        rowDate <= appliedFilters.dateRange.endDate;
      const matchesCreditDebit =
        appliedFilters.creditDebit === "All" ||
        row.type === appliedFilters.creditDebit;
      const matchesTransactionType =
        appliedFilters.transactionType === "All" ||
        (appliedFilters.transactionType === "Payout" &&
          row.referenceId.startsWith("PAYOUT")) ||
        (appliedFilters.transactionType === "Add Money" &&
          row.referenceId.startsWith("ADD_MONEY")) ||
        (appliedFilters.transactionType === "Internal Transfer" &&
          row.referenceId.startsWith("INTERNAL_TRF"));
      const matchesSearch =
        !query ||
        getStatementSearchValue(row, appliedFilters.searchField)
          .toLowerCase()
          .includes(query);

      return (
        matchesAccount &&
        matchesStatus &&
        matchesDate &&
        matchesCreditDebit &&
        matchesTransactionType &&
        matchesSearch
      );
    });
  }, [appliedFilters, expandedStatementRows]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / pageSize));
  const visibleRows = filteredRows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );
  const resultStart = filteredRows.length
    ? (currentPage - 1) * pageSize + 1
    : 0;
  const resultEnd = Math.min(currentPage * pageSize, filteredRows.length);

  const clearFilters = () => {
    setAccountFilter("All Accounts");
    setTransactionTypeFilter("All");
    setCreditDebitFilter("All");
    setStatusFilter("All");
    setSearchQuery("");
    setSearchField("Reference ID");
    setDateRangeFilter(defaultDateRange);
    setAppliedFilters({
      account: "All Accounts",
      transactionType: "All",
      creditDebit: "All",
      status: "All",
      search: "",
      searchField: "Reference ID",
      dateRange: defaultDateRange,
    });
    setCurrentPage(1);
  };

  const exportStatements = () => {
    const headers = [
      "Date & Time",
      "Reference ID",
      "UTR Number",
      "Narration",
      "Account",
      "Type",
      "Amount",
      "Balance",
      "Status",
    ];
    const csvRows = filteredRows.map((row) =>
      [
        row.dateTime,
        row.referenceId,
        row.utrNumber,
        row.narration,
        `${row.account} **** ${row.accountNumber}`,
        row.type,
        row.amount,
        row.balance,
        row.status,
      ]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(","),
    );
    const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "payout-statements.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const tableText = (value: string) => value.trim() || "-";
  const maskedLast4 = (accountNumber: string) => {
    const last4 = accountNumber.replace(/\D/g, "").slice(-4);
    return last4 ? `****${last4}` : "-";
  };
  const formatChipDate = (date: Date) =>
    date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  const dateChipLabel = `${formatChipDate(appliedFilters.dateRange.startDate)} - ${formatChipDate(appliedFilters.dateRange.endDate)}`;
  const isDefaultDateRange =
    appliedFilters.dateRange.startDate.getTime() === defaultDateRange.startDate.getTime() &&
    appliedFilters.dateRange.endDate.getTime() === defaultDateRange.endDate.getTime();

  const statementTableRows = visibleRows.map((row) => [
    <PayoutTableCell
      value={tableText(row.dateTime)}
      onClick={() => setSelectedStatement(row)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <PayoutTableCell
      value={tableText(row.referenceId)}
      onClick={() => setSelectedStatement(row)}
      valueClassName="font-semibold text-[#6687FF]"
    />,
    <PayoutTableCell
      value={tableText(row.utrNumber)}
      onClick={() => setSelectedStatement(row)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <PayoutTableCell
      value={tableText(row.narration)}
      onClick={() => setSelectedStatement(row)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <span>
      <PayoutTableCell
        value={tableText(row.account)}
        copyValue={[tableText(row.account), maskedLast4(row.accountNumber)].join(" ")}
        onClick={() => setSelectedStatement(row)}
        valueClassName="font-medium text-[#1B163B]"
      />
      <span className="mt-1 block max-w-[220px] truncate tracking-[2px] text-[#505E8E]" title={maskedLast4(row.accountNumber)}>
        {maskedLast4(row.accountNumber)}
      </span>
    </span>,
    <button
      type="button"
      onClick={() => setSelectedStatement(row)}
      className="block w-full text-left"
    >
      <span
        className={
          "rounded-[5px] px-2 py-1 text-xs font-semibold " +
          (typeClassName[row.type] || "bg-[#EEF2F7] text-[#505E8E]")
        }
      >
        {tableText(row.type)}
      </span>
    </button>,
    <PayoutTableCell
      value={`${row.amount < 0 ? "-" : "+"} ${formatINR(Math.abs(row.amount))}`}
      onClick={() => setSelectedStatement(row)}
      valueClassName={
        "font-semibold " + (row.amount < 0 ? "text-[#EF4444]" : "text-[#16A34A]")
      }
    />,
    <PayoutTableCell
      value={formatINR(row.balance)}
      onClick={() => setSelectedStatement(row)}
      valueClassName="font-medium text-[#1B163B]"
    />,
    <button
      type="button"
      onClick={() => setSelectedStatement(row)}
      className="block w-full text-left"
    >
      <span
        className={
          "rounded-[5px] px-2 py-1 text-xs font-semibold " +
          (statusClassName[row.status] || "bg-[#EEF2F7] text-[#505E8E]")
        }
      >
        {tableText(row.status)}
      </span>
    </button>,
    <div className="payout-action-cell payout-action-cell--single">
      <button
        type="button"
        onClick={() => setSelectedStatement(row)}
        className="payout-action-icon"
        aria-label={`View details for ${row.referenceId}`}
        title="View details"
      >
        <FiEye className="text-sm" />
      </button>
    </div>,
  ]);

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Statements
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            View and download statements for all your payout accounts
          </p>
        </div>
      </div>

      <section className="payout-filter-card mb-5 rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
        <div className="payout-filter-grid mb-5 pb-1">
            <FilterSelect
              label="Account"
              value={accountFilter}
              options={accountOptions}
              onChange={setAccountFilter}
              wide
              className="min-w-0"
            />
            <PayoutDateRangeFilter
              label="Date Range"
              displayText="12/03/2024 - 18/07/2025"
              onChange={setDateRangeFilter}
              className="min-w-0"
            />
            <FilterSelect
              label="Transaction Type"
              value={transactionTypeFilter}
              options={["All", "Payout", "Add Money", "Internal Transfer"]}
              onChange={setTransactionTypeFilter}
              wide
              className="min-w-0"
            />
            <FilterSelect
              label="Credit / Debit"
              value={creditDebitFilter}
              options={["All", "Credit", "Debit"]}
              onChange={setCreditDebitFilter}
              wide
              className="min-w-0"
            />
            <FilterSelect
              label="Status"
              value={statusFilter}
              options={["All", "Processed", "Pending", "Failed"]}
              onChange={setStatusFilter}
              wide
              className="min-w-0"
            />
        </div>

        <div className="flex flex-wrap items-end gap-3">
          <PayoutSearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            filterBy={searchField}
            onFilterByChange={setSearchField}
            filterOptions={statementSearchOptions}
            placeholder={`Search by ${searchField}`}
            className="min-w-[320px] max-w-[500px] flex-1"
          />
          <PayoutFilterActions
            onClear={clearFilters}
            onExport={exportStatements}
          />
        </div>
        <PayoutFilterChips
          className="mt-3"
          onClearAll={clearFilters}
          chips={[
            {
              label: "Account",
              value: appliedFilters.account,
              onRemove: () => setAccountFilter("All Accounts"),
            },
            {
              label: "Transaction Type",
              value: appliedFilters.transactionType,
              onRemove: () => setTransactionTypeFilter("All"),
            },
            {
              label: "Credit / Debit",
              value: appliedFilters.creditDebit,
              onRemove: () => setCreditDebitFilter("All"),
            },
            {
              label: "Status",
              value: appliedFilters.status,
              onRemove: () => setStatusFilter("All"),
            },
            {
              label: "Date Range",
              value: dateChipLabel,
              hidden: isDefaultDateRange,
              onRemove: () => setDateRangeFilter(defaultDateRange),
            },
            {
              label: "Search",
              value: appliedFilters.search,
              onRemove: () => setSearchQuery(""),
            },
          ]}
        />
      </section>

      <div className="mb-5">
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
          {visibleStatementSummary.map((item) => (
            <div
              key={item.label}
              className="flex min-h-[112px] items-center gap-4 rounded-[8px] border border-[#E0E5F2] bg-white px-4 py-5 new-shadow"
            >
              <div
                className={
                  "payout-kpi-icon flex h-9 w-9 shrink-0 items-center justify-center text-lg " +
                  item.tone
                }
              >
                {item.icon}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[#505E8E]">
                  {item.label}
                </p>
                <p className="mt-2 text-[18px] font-semibold text-[#1B163B]">
                  {formatINR(item.value)}
                </p>
                <p className="mt-3 text-xs font-medium text-[#505E8E]">
                  {item.detail}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <PayoutDataTable
        title="Transactions"
        minWidth="min-w-[1240px]"
        columns={[
          "Date & Time",
          "Reference ID",
          "UTR Number",
          "Narration",
          "Account",
          "Type",
          "Amount",
          "Balance",
          "Status",
          "Actions",
        ]}
        rows={statementTableRows}
        emptyState={
          <PayoutEmptyState
            icon={<LuWalletCards />}
            title="No statements yet"
            description="Statement transactions will appear here after money moves through a payout account."
            action={
              canAccess("transfers.create") ? (
                <Link
                  to="/payouts/transfers/initiate"
                  className="flex h-10 items-center rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
                >
                  Initiate Transfer
                </Link>
              ) : null
            }
            secondaryAction={
              <Link
                to="/payouts/accounts"
                className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
              >
                View Accounts
              </Link>
            }
          />
        }
        footer={
          <div className="flex min-h-[64px] w-full items-center justify-between gap-4 border-t border-[#E0E5F2] px-5 py-4 text-xs font-medium text-[#505E8E]">
            <span className="min-w-max">
              Showing {resultStart} to {resultEnd} of {filteredRows.length} results
            </span>
            <div className="ml-auto flex min-w-max items-center gap-4">
              <button className="flex h-9 min-w-[120px] items-center justify-between rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-medium text-[#1B163B]">
                10 per page
                <FiChevronDown />
              </button>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() =>
                    setCurrentPage((page) => Math.max(1, page - 1))
                  }
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowLeft className="text-xs" />
                </button>
                {Array.from({ length: Math.min(pageCount, 3) }, (_, index) => {
                  const page = index + 1;
                  return (
                    <button
                      type="button"
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={
                        "h-9 w-9 rounded-md " +
                        (currentPage === page
                          ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                          : "border border-[#E0E5F2] text-[#1B163B]")
                      }
                    >
                      {page}
                    </button>
                  );
                })}
                {pageCount > 5 && (
                  <>
                    <span className="px-2 text-[#505E8E]">...</span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(pageCount)}
                      className={
                        "h-9 w-9 rounded-md " +
                        (currentPage === pageCount
                          ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                          : "border border-[#E0E5F2] text-[#1B163B]")
                      }
                    >
                      {pageCount}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  disabled={currentPage === pageCount}
                  onClick={() =>
                    setCurrentPage((page) => Math.min(pageCount, page + 1))
                  }
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        }
      />
      <StatementDetailsDrawer
        row={selectedStatement}
        onClose={() => setSelectedStatement(null)}
      />
    </div>
  );
}

export default PayoutStatements;
